"use client";

import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { useTimePilotT } from "@/i18n/useTimePilotT";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import {
  DownloadSimpleIcon,
  FileCloudIcon,
  FunnelIcon,
  ListPlusIcon,
  NewspaperClippingIcon,
  CheckIcon,
  CaretDoubleDownIcon,
  CaretDoubleUpIcon,
  FilesIcon,
  NotePencilIcon,
  ClipboardTextIcon,
  CameraIcon,
  CopyIcon,
  TrashIcon,
  Article,
  FrameCornersIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  markdownRehypeRawSanitizePlugins,
  markdownRehypeSanitizePlugins,
} from "@/lib/markdown-rehype-plugins";
import { Button } from "@tranquility-ai/theme-components/button";
import { Chip, ChipGroup } from "@tranquility-ai/theme-components/chip";
import { Combobox } from "@tranquility-ai/theme-components/combobox";
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import MediaDisplay from "./ui/mediaDisplay";
import { MediaPreviewSlot } from "./ui/media-preview-slot";
import { EmailAttachmentsList } from "./email-attachments-list";
import { PanelListCard } from "@tranquility-ai/theme-components/panel-list-card";
import { SIDE_PANEL_ABOVE_PRIMARY_NAV_POPOVER_Z_CLASS, SIDE_PANEL_BODY_INSET_CLASS, SIDE_PANEL_DIVIDER_BOTTOM_CLASS, SIDE_PANEL_DETAIL_INSET_CLASS, SIDE_PANEL_FAVORITES_LIST_SCROLL_CLASS, SIDE_PANEL_LIST_CLASS, SIDE_PANEL_LIST_SCROLL_CLASS, SIDE_PANEL_SPLIT_BORDER_CLASS } from "./side-panel";
import type { MediaShareEntry } from "./mediaSourceShare";
import { mediaShareEntryToMediaSource, useMediaSourceShare } from "./mediaSourceShare";
import type { ResearchNote } from "./research-notes";
import type { CopyHistoryEntry } from "./recently-copied";
import {
  type ToolsSharedCaptureEntry,
} from "./tools-shared-case-data";
import {
  captureEntryDisplayUrl,
  captureEntryImageRef,
} from "@/shared/userImageStorage";
import { obfuscatedWebpEntryToDataUrl } from "./tools-captures";
import { useToolsDrawer } from "./tools-drawer";
import { CsamDetectionPill } from "./ui/media/csam-detection-pill";
import { ContentModerationPreviewSwitcher } from "./ui/content-moderation-preview-switcher";
import { ContentModerationAckModal } from "./ui/content-moderation-ack-modal";
import { useCsamAccess } from "@/lib/csam-access";
import { useJsonMediaDisplayEnabled } from '@/hooks/useMediaPlayerTransition';
import type { MediaSource, Source } from "@/shared/types";
import {
  handleDownload as handleDownloadSource,
  isRestrictedFileType,
  pathForAssistantScope,
} from "@/shared/functions";
import { useCellServiceSession } from "@/hooks/useCellServiceSession";
import type { EventFromChat } from "./answer-item";
import {
  emailBodyMediaItem,
  emailFetchCacheKey,
  fetchEmailCitation,
  isMboxEmailJsonUrl,
  resolveEmailFetchArgs,
  urlsLikelySameAttachment,
  urlLooksLikeEml,
  urlLooksLikeMbox,
} from "@/shared/email-citation";
import { resolveSourceForActions } from "@/shared/resolve-source-for-actions";
import type { FileSummary, MboxAttachment } from "@/shared/types";

dayjs.extend(relativeTime);

const MarkdownEditor = dynamic(
  () => import("@tranquility-ai/theme-components/markdown-editor").then((m) => m.MarkdownEditor),
  { ssr: false },
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FavoriteItemType = "source" | "note" | "copy" | "capture";

export interface FavoriteItem {
  id: string;
  type: FavoriteItemType;
  timestamp: number;
  data: MediaShareEntry | ResearchNote | CopyHistoryEntry | ToolsSharedCaptureEntry;
}

export type DateRange = "all" | "24h" | "7d" | "30d";

const DATE_RANGE_VALUES = ["all", "24h", "7d", "30d"] as const;

const DATE_RANGE_LABEL: Record<(typeof DATE_RANGE_VALUES)[number], string> = {
  all: "favorites.dateRange.all",
  "24h": "favorites.dateRange.h24",
  "7d": "favorites.dateRange.d7",
  "30d": "favorites.dateRange.d30",
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

export function mergeAllFavorites(
  entries: MediaShareEntry[],
  notes: ResearchNote[],
  copyHistory: CopyHistoryEntry[],
  captures: ToolsSharedCaptureEntry[],
): FavoriteItem[] {
  const items: FavoriteItem[] = [];

  for (const e of entries) {
    items.push({ id: e.id, type: "source", timestamp: e.timestamp, data: e });
  }
  for (const n of notes) {
    items.push({ id: n.id, type: "note", timestamp: n.updatedAt || n.createdAt, data: n });
  }
  for (const c of copyHistory) {
    items.push({ id: c.id, type: "copy", timestamp: c.timestamp, data: c });
  }
  for (const cap of captures) {
    items.push({ id: cap.id, type: "capture", timestamp: cap.createdAt, data: cap });
  }

  items.sort((a, b) => b.timestamp - a.timestamp);
  return items;
}

export function filterFavorites(
  items: FavoriteItem[],
  selectedTypes: Set<FavoriteItemType>,
  dateRange: DateRange,
): FavoriteItem[] {
  const now = Date.now();
  const cutoff =
    dateRange === "all" ? 0
    : dateRange === "24h" ? now - 86_400_000
    : dateRange === "7d" ? now - 604_800_000
    : now - 2_592_000_000;

  return items
    .filter((item) => selectedTypes.has(item.type))
    .filter((item) => item.timestamp >= cutoff);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PREVIEW_MAX_CHARS = 80;

function previewText(text: string, max = PREVIEW_MAX_CHARS): string {
  const t = text.trim();
  return t.length <= max ? t : t.slice(0, max) + "…";
}

/** Mailbox / junkdrawer body paths must not be handed to MediaDisplay as media. */
function isMailboxDisplayUrl(url?: string | null): boolean {
  return urlLooksLikeMbox(url) || isMboxEmailJsonUrl(url);
}

/** Raw ``.eml`` is not a viewer-ready body; wait for the fetched markdown/text. */
function isRawEmailExtractUrl(url?: string | null): boolean {
  return isMailboxDisplayUrl(url) || urlLooksLikeEml(url);
}

function firstNonMailboxUrl(...urls: Array<string | null | undefined>): string | undefined {
  return urls.find((url): url is string => !!url?.trim() && !isMailboxDisplayUrl(url));
}

function firstRenderableEmailBodyUrl(
  ...urls: Array<string | null | undefined>
): string | undefined {
  return urls.find((url): url is string => !!url?.trim() && !isRawEmailExtractUrl(url));
}

function stripMailboxUrl(url?: string | null): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed || isMailboxDisplayUrl(trimmed)) return undefined;
  return trimmed;
}

function sourceLabel(e: MediaShareEntry): string {
  const p = e.truncatedFilePath;
  if (p && p.trim()) return p;
  const u = e.mediaUrl || e.mediaSrc || "";
  if (!u) return "Unknown";
  const parts = u.split("/");
  return parts[parts.length - 1] || u;
}

function noteTitle(note: ResearchNote, untitled: string): string {
  const title = (note.title || "").trim();
  return title || untitled;
}

const TYPE_META: Record<FavoriteItemType, { Icon: React.ComponentType<any>; color: string }> = {
  source: { Icon: FilesIcon, color: "text-blue-400" },
  note: { Icon: NotePencilIcon, color: "text-amber-400" },
  copy: { Icon: ClipboardTextIcon, color: "text-green-400" },
  capture: { Icon: CameraIcon, color: "text-purple-400" },
};

const FILTER_TAB_KEY: Record<FavoriteItemType, string> = {
  source: "favorites.type.sources",
  note: "favorites.type.notes",
  copy: "favorites.type.copied",
  capture: "favorites.type.captures",
};

const CARD_TYPE_KEY: Record<FavoriteItemType, string> = {
  source: "favorites.card.source",
  note: "favorites.card.note",
  copy: "favorites.card.copy",
  capture: "favorites.card.capture",
};

function itemPreview(item: FavoriteItem, untitled: string, captureLabel: string): string {
  switch (item.type) {
    case "source":
      return previewText(sourceLabel(item.data as MediaShareEntry));
    case "note":
      return previewText(noteTitle(item.data as ResearchNote, untitled));
    case "copy":
      return previewText((item.data as CopyHistoryEntry).text);
    case "capture": {
      const cap = item.data as ToolsSharedCaptureEntry;
      if (cap.s3Uri?.trim()) {
        const tail = cap.s3Uri.split("/").pop();
        return tail ? `${captureLabel} · ${tail}` : `${captureLabel} · S3`;
      }
      if (cap.webpBase64) {
        return `${captureLabel} · ${(cap.webpBase64.length / 1024).toFixed(0)} KB`;
      }
      return captureLabel;
    }
  }
}

// ---------------------------------------------------------------------------
// FilterBar
// ---------------------------------------------------------------------------

function FilterBar({
  selectedTypes,
  setSelectedTypes,
  dateRange,
  setDateRange,
  availableTypes,
  compact = false,
}: {
  selectedTypes: Set<FavoriteItemType>;
  setSelectedTypes: React.Dispatch<React.SetStateAction<Set<FavoriteItemType>>>;
  dateRange: DateRange;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
  availableTypes: Set<FavoriteItemType>;
  /** Narrow list-only panel (no detail column): stack rows flush left. */
  compact?: boolean;
}) {
  const t = useTimePilotT();
  const toggleType = (type: FavoriteItemType) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        if (next.size > 1) next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const isFiltered = selectedTypes.size < availableTypes.size || dateRange !== "all";

  const viewingFilters = (
    <>
      <span className="text-[var(--text-inverse)]/50 text-xs font-medium mr-0.5 shrink-0">{t("favorites.viewing")}</span>
      <ChipGroup className="min-w-0 flex-wrap">
        {(Object.keys(TYPE_META) as FavoriteItemType[]).filter((type) => availableTypes.has(type)).map((type) => {
          const { Icon } = TYPE_META[type];
          const active = selectedTypes.has(type);
          return (
            <Chip
              key={type}
              type="filter"
              surface="inverse"
              size="sm"
              label={t(FILTER_TAB_KEY[type])}
              leadingIcon={<Icon weight="duotone" />}
              selected={active}
              onToggle={() => toggleType(type)}
            />
          );
        })}
      </ChipGroup>
    </>
  );

  const savedFilter = (
    <div className={`flex items-center gap-1.5 min-w-0 ${compact ? "w-full" : "shrink-0"}`}>
      <span className="text-[var(--text-inverse)]/50 text-xs font-medium shrink-0">{t("favorites.saved")}</span>
      <Combobox
        aria-label={t("favorites.dateRange.aria")}
        value={dateRange}
        onValueChange={(value) => setDateRange((value ?? "all") as DateRange)}
        options={DATE_RANGE_VALUES.map((value) => ({ value, label: t(DATE_RANGE_LABEL[value]) }))}
        searchable={false}
        className={compact ? "min-w-0 flex-1" : "min-w-[9rem]"}
        menuClassName={SIDE_PANEL_ABOVE_PRIMARY_NAV_POPOVER_Z_CLASS}
      />
    </div>
  );

  return (
    <div className={`flex flex-col gap-4 shrink-0 ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS} ${SIDE_PANEL_BODY_INSET_CLASS}`}>
      {compact ? (
        <div className="flex flex-col items-stretch gap-3 min-w-0">
          <div className="flex items-center gap-x-1.5 gap-y-1.5 flex-wrap min-w-0">
            {viewingFilters}
          </div>
          {savedFilter}
        </div>
      ) : (
        <div className="flex items-center gap-x-1.5 gap-y-1.5 flex-wrap min-w-0">
          {viewingFilters}
          <div className="flex-1 min-w-2" />
          {savedFilter}
        </div>
      )}
      {isFiltered && (
        <Button
          type="button"
          style="text"
          color="light"
          background="inverse"
          size="sm"
          onClick={() => { setSelectedTypes(new Set(availableTypes)); setDateRange("all"); }}
          className="text-xs text-[var(--text-inverse)]/50 hover:text-[var(--text-inverse)]/80 self-start"
        >
          {t("favorites.clearFilters")}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FavoriteCard
// ---------------------------------------------------------------------------

function FavoriteCard({
  item,
  isSelected,
  onSelect,
}: {
  item: FavoriteItem;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const t = useTimePilotT();
  const { Icon, color } = TYPE_META[item.type];
  const cardTitle = t(CARD_TYPE_KEY[item.type]);

  const { canSeeCsamTags } = useCsamAccess();
  const captureEntry = item.type === "capture" ? (item.data as ToolsSharedCaptureEntry) : null;
  const captureObfuscatedUrl =
    canSeeCsamTags && captureEntry ? obfuscatedWebpEntryToDataUrl(captureEntry) : null;
  const captureCmType = canSeeCsamTags ? captureEntry?.contentModerationType : undefined;

  return (
    <li className="list-none">
      <PanelListCard
        isSelected={isSelected}
        onClick={onSelect}
        leadingIcon={<Icon size={16} weight="duotone" className={color} />}
        label={cardTitle}
        title={itemPreview(item, t("researchNotes.untitled"), t("favorites.card.capture"))}
        badge={
          captureCmType ? (
            <CsamDetectionPill type={captureCmType} />
          ) : undefined
        }
        mediaSlot={
          item.type === "capture" ? (
            <span className="block aspect-[3/2] w-full overflow-hidden rounded-[var(--radius-rounded-small)] bg-black/40">
              <img
                src={captureObfuscatedUrl ?? captureEntryDisplayUrl(item.data as ToolsSharedCaptureEntry)}
                alt=""
                className="h-full w-full object-cover"
              />
            </span>
          ) : undefined
        }
        meta={
          <time dateTime={new Date(item.timestamp).toISOString()} title={new Date(item.timestamp).toLocaleString()}>
            {dayjs(item.timestamp).fromNow()}
          </time>
        }
      />
    </li>
  );
}

// ---------------------------------------------------------------------------
// Detail Views
// ---------------------------------------------------------------------------

function SourceDetailView({
  entry,
  caseId,
  onRemove,
  onClose,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady = false,
}: {
  entry: MediaShareEntry;
  caseId?: string;
  onRemove: () => void;
  onClose?: () => void;
  onAddToTimeline?: (event: EventFromChat) => void;
  addSharedSourceToEvent?: (source: MediaSource) => boolean;
  eventSharedSourceAddReady?: boolean;
}) {
  const t = useTimePilotT();
  const [addedToScopeId, setAddedToScopeId] = useState<string | null>(null);
  const [copiedPathId, setCopiedPathId] = useState<string | null>(null);
  const [createdEventId, setCreatedEventId] = useState<string | null>(null);
  const [justAddedToEventSources, setJustAddedToEventSources] = useState(false);
  const [isCitationExpanded, setIsCitationExpanded] = useState(true);
  const [copiedAttachmentPathIndex, setCopiedAttachmentPathIndex] = useState<number | null>(null);
  const [createdAttachmentEventIndex, setCreatedAttachmentEventIndex] = useState<number | null>(null);
  const [addedAttachmentFavoriteIndex, setAddedAttachmentFavoriteIndex] = useState<number | null>(null);
  const [emailUrl, setEmailUrl] = useState<string | null>(null);
  const [emailAttachments, setEmailAttachments] = useState<MboxAttachment[] | null>(null);
  const [emailFileInfo, setEmailFileInfo] = useState<FileSummary | null>(null);

  const jsonMediaDisplayEnabled = useJsonMediaDisplayEnabled();
  const mediaShare = useMediaSourceShare();
  const previewHostRef = useRef<HTMLDivElement>(null);
  const [previewBox, setPreviewBox] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => { setIsCitationExpanded(true); }, [entry.id]);

  const hasCitationBody = Boolean(
    entry.content &&
      entry.content.trim() !== "" &&
      !entry.location?.includes("/bkb/slow/transcribe/") &&
      !entry.location?.endsWith(".transcript.txt"),
  );
  const hasPageLabel = Boolean(entry.page && !isNaN(parseInt(entry.page)));
  // Only an expanded body competes with the preview for height; a bare page
  // label is one line and should not cost the media half the panel.
  const splitWithMedia = hasCitationBody && isCitationExpanded;

  const { cellId, accessToken: cellAccessToken } = useCellServiceSession();

  const emailFetchArgs = useMemo(() => resolveEmailFetchArgs({
    originalUrl: entry.originalUrl,
    sourceFilePath: entry.sourceFilePath,
    sourceUrl: entry.sourceUrl,
    mediaUrl: entry.mediaUrl,
    mediaSrc: entry.mediaSrc,
    previewFilePath: entry.previewFilePath,
    analysisFilePath: entry.analysisFilePath,
    dispatchedFilePath: entry.dispatchedFilePath,
    mediaContext: entry.mediaContext as Record<string, unknown> | undefined,
  }), [
    entry.originalUrl,
    entry.sourceFilePath,
    entry.sourceUrl,
    entry.mediaUrl,
    entry.mediaSrc,
    entry.previewFilePath,
    entry.analysisFilePath,
    entry.dispatchedFilePath,
    entry.mediaContext,
  ]);
  const isEmailSource = emailFetchArgs != null;
  // Video and audio size themselves. A PDF (and other paged media) does not:
  // without a measured box its page height resolves to 0 and stays blank until
  // maximize, which sizes from the viewport instead.
  const sizedPreview =
    !isEmailSource && entry.mediaType !== "video" && entry.mediaType !== "audio";

  useLayoutEffect(() => {
    if (!sizedPreview) return;
    const host = previewHostRef.current;
    if (!host) return;
    const measure = () => {
      const width = Math.floor(host.clientWidth);
      const height = Math.floor(host.clientHeight);
      if (width <= 0 || height <= 0) return;
      setPreviewBox((prev) =>
        prev?.width === width && prev?.height === height ? prev : { width, height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, [sizedPreview, entry.id, splitWithMedia]);

  const emailFetchKey = emailFetchCacheKey(emailFetchArgs);

  useEffect(() => {
    if (!emailFetchArgs || !cellId || !cellAccessToken) {
      setEmailUrl(null);
      setEmailAttachments(null);
      setEmailFileInfo(null);
      return;
    }
    const abortController = new AbortController();
    const fetchArgs = emailFetchArgs;
    setEmailUrl(null);
    setEmailAttachments(null);
    setEmailFileInfo(null);
    void fetchEmailCitation(
      fetchArgs,
      { cellId, accessToken: cellAccessToken },
      abortController.signal,
    ).then((payload) => {
      if (abortController.signal.aborted) return;
      setEmailUrl(payload?.emailUrl ?? null);
      setEmailAttachments(payload?.attachments?.length ? payload.attachments : null);
      setEmailFileInfo(payload?.fileInfo ?? null);
    });
    return () => abortController.abort();
  }, [emailFetchArgs, emailFetchKey, cellId, cellAccessToken]);

  const entryToSource = useCallback(
    (e: MediaShareEntry): Source => ({
      sourceFilePath:
        e.sourceFilePath || e.sourceUrl || e.mediaUrl || e.mediaSrc,
      sourceUrl:
        e.sourceUrl || e.sourceFilePath || e.mediaUrl || e.mediaSrc,
      originalUrl: e.originalUrl,
      dispatchedFilePath: e.dispatchedFilePath,
      analysisFilePath: e.analysisFilePath,
      previewFilePath: e.previewFilePath,
      truncatedFilePath: e.truncatedFilePath,
      sourceContext: e.mediaContext,
      fileInfo: e.fileInfo,
      page: e.page ?? e.mediaContext?.pageNumber?.toString(),
      content: e.content,
      location: e.location,
    }),
    [],
  );

  const sourceForActions = useMemo(
    () => resolveSourceForActions(entryToSource(entry)),
    [entry, entryToSource],
  );

  const handleDownload = useCallback(() => {
    const session = cellId && cellAccessToken ? { cellId, accessToken: cellAccessToken } : undefined;
    handleDownloadSource(sourceForActions, session);
  }, [sourceForActions, cellId, cellAccessToken]);

  const handleAddToScope = useCallback(() => {
    const path = pathForAssistantScope(sourceForActions);
    if (!path || !caseId) return;
    const key = `${caseId}-scopesObject`;
    const current = JSON.parse(sessionStorage.getItem(key) || '{ "scopes": [], "files": [], "excludedScopes": [], "excludedFiles": [] }');
    current.files = Array.from(new Set([...current.files, path]));
    sessionStorage.setItem(key, JSON.stringify(current));
    window.dispatchEvent(new CustomEvent("scopes-updated", { detail: { caseId } }));
    setAddedToScopeId(entry.id);
    setTimeout(() => setAddedToScopeId(null), 1500);
  }, [entry, sourceForActions, caseId]);

  const handleCopyPath = useCallback(async () => {
    const path =
      sourceForActions.sourceFilePath ||
      entry.truncatedFilePath ||
      entry.mediaUrl ||
      entry.mediaSrc ||
      "";
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      setCopiedPathId(entry.id);
      setTimeout(() => setCopiedPathId(null), 1500);
    } catch { /* ignore */ }
  }, [entry, sourceForActions]);

  const handleCreateEvent = useCallback(() => {
    if (!onAddToTimeline || !caseId) return;
    const source = sourceForActions;
    const event: EventFromChat = {
      answerText: "FROM_SOURCE",
      sources: [source],
      media: [entry as MediaSource],
      autogen: false,
      epochTimeSecs: Math.floor(Date.now() / 1000),
    };
    onAddToTimeline(event);
    setCreatedEventId(entry.id);
    setTimeout(() => setCreatedEventId(null), 1500);
  }, [entry, onAddToTimeline, caseId, sourceForActions]);

  const handleAddToEventSources = useCallback(() => {
    if (!addSharedSourceToEvent || !eventSharedSourceAddReady) return;
    const source = mediaShareEntryToMediaSource(entry);
    if (!source) return;
    const ok = addSharedSourceToEvent(source);
    if (!ok) return;
    setJustAddedToEventSources(true);
    setTimeout(() => setJustAddedToEventSources(false), 1500);
  }, [entry, addSharedSourceToEvent, eventSharedSourceAddReady]);

  const explicitAttachmentUrl = (
    (typeof entry.mediaContext?.attachmentUrl === "string" && entry.mediaContext.attachmentUrl) ||
    entry.analysisFilePath ||
    ""
  ).trim();
  const inferredAttachmentUrl =
    entry.mediaType !== "text"
      ? (
          firstRenderableEmailBodyUrl(
            entry.sourceFilePath,
            entry.dispatchedFilePath,
            entry.analysisFilePath,
            entry.mediaUrl,
            entry.mediaSrc,
          ) || ""
        ).trim()
      : "";
  const citedAttachmentUrl = (explicitAttachmentUrl || inferredAttachmentUrl).trim();
  const entryDisplayUrl = (entry.mediaUrl || entry.mediaSrc || entry.sourceFilePath || "").trim();
  const isFavoritedEmailAttachment =
    !!citedAttachmentUrl &&
    !urlLooksLikeEml(citedAttachmentUrl) &&
    !isMboxEmailJsonUrl(citedAttachmentUrl) &&
    (urlsLikelySameAttachment(entryDisplayUrl, citedAttachmentUrl) ||
      urlsLikelySameAttachment(entry.sourceFilePath, citedAttachmentUrl) ||
      urlsLikelySameAttachment(entry.dispatchedFilePath, citedAttachmentUrl) ||
      urlsLikelySameAttachment(entry.analysisFilePath, citedAttachmentUrl));

  const emailDisplayUrl = useMemo(() => {
    if (!isEmailSource) return undefined;
    if (isFavoritedEmailAttachment) {
      const matchingPreviewPath =
        entry.previewFilePath && urlsLikelySameAttachment(entry.previewFilePath, citedAttachmentUrl)
          ? entry.previewFilePath
          : undefined;
      const displayMatchedAttachment = firstNonMailboxUrl(
        matchingPreviewPath,
        entry.mediaUrl && urlsLikelySameAttachment(entry.mediaUrl, citedAttachmentUrl)
          ? entry.mediaUrl
          : undefined,
        entry.mediaSrc && urlsLikelySameAttachment(entry.mediaSrc, citedAttachmentUrl)
          ? entry.mediaSrc
          : undefined,
        entry.sourceFilePath && urlsLikelySameAttachment(entry.sourceFilePath, citedAttachmentUrl)
          ? entry.sourceFilePath
          : undefined,
      );
      return firstNonMailboxUrl(
        displayMatchedAttachment,
        citedAttachmentUrl,
        matchingPreviewPath,
        entry.analysisFilePath,
        entry.sourceFilePath,
        entry.dispatchedFilePath,
        entry.mediaUrl,
        entry.mediaSrc,
      );
    }
    return firstRenderableEmailBodyUrl(
      emailUrl,
      entry.previewFilePath,
      entry.mediaUrl,
      entry.mediaSrc,
      entry.sourceFilePath,
    );
  }, [
    isEmailSource,
    isFavoritedEmailAttachment,
    emailUrl,
    citedAttachmentUrl,
    entry.mediaUrl,
    entry.mediaSrc,
    entry.sourceFilePath,
    entry.analysisFilePath,
    entry.previewFilePath,
  ]);

  const visibleEmailAttachments = useMemo(() => {
    if (!emailAttachments?.length) return [];
    const hidden = new Set(
      [
        emailUrl,
        emailDisplayUrl,
        isFavoritedEmailAttachment ? citedAttachmentUrl : null,
        entry.sourceFilePath,
        entry.previewFilePath,
        entry.mediaUrl,
        entry.mediaSrc,
      ]
        .map((url) => url?.trim())
        .filter((url): url is string => !!url && !isMailboxDisplayUrl(url)),
    );
    return emailAttachments.filter((attachment) => {
      const url = attachment.attachmentUrl?.trim() ?? "";
      if (!url || isMboxEmailJsonUrl(url)) return false;
      if (hidden.has(url)) return false;
      return !Array.from(hidden).some((hiddenUrl) => urlsLikelySameAttachment(hiddenUrl, url));
    });
  }, [
    emailAttachments,
    emailUrl,
    emailDisplayUrl,
    isFavoritedEmailAttachment,
    citedAttachmentUrl,
    entry.sourceFilePath,
    entry.previewFilePath,
    entry.mediaUrl,
    entry.mediaSrc,
  ]);

  const citedAttachmentOpenIndex = useMemo(() => {
    if (!citedAttachmentUrl || isFavoritedEmailAttachment) return null;
    return (
      visibleEmailAttachments.find((attachment) =>
        urlsLikelySameAttachment(attachment.attachmentUrl, citedAttachmentUrl),
      )?.attachmentIndex ?? null
    );
  }, [citedAttachmentUrl, isFavoritedEmailAttachment, visibleEmailAttachments]);

  const emailMediaItem = useMemo(() => {
    const mediaContext = {
      ...entry.mediaContext,
      htmlChunk: entry.content ? entry.content.match(/page \d+/i)?.[0] || "" : "",
      pageNumber: entry.page ? parseInt(entry.page, 10) : entry.mediaContext?.pageNumber ?? 1,
    };
    if (!isEmailSource) {
      return { ...entry, mediaContext } as MediaSource;
    }
    const stripped = {
      ...entry,
      sourceFilePath: stripMailboxUrl(entry.sourceFilePath),
      sourceUrl: stripMailboxUrl(entry.sourceUrl),
      // Attachment favorites should not inherit the parent email markdown/text
      // preview path, which can outrank the attachment URL in display selection.
      previewFilePath:
        isFavoritedEmailAttachment && !urlsLikelySameAttachment(entry.previewFilePath, citedAttachmentUrl)
          ? undefined
          : stripMailboxUrl(entry.previewFilePath),
      dispatchedFilePath: entry.dispatchedFilePath,
      mediaContext,
    };
    if (emailUrl && !isFavoritedEmailAttachment) {
      return emailBodyMediaItem(
        {
          ...stripped,
          originalUrl: emailDisplayUrl,
          mediaUrl: emailDisplayUrl ?? stripMailboxUrl(entry.mediaUrl),
          mediaSrc: emailDisplayUrl ?? stripMailboxUrl(entry.mediaSrc),
        } as MediaSource,
        emailUrl,
        emailFileInfo,
      );
    }
    if (emailDisplayUrl) {
      const attachmentPinnedFields = isFavoritedEmailAttachment
        ? {
            sourceFilePath: emailDisplayUrl,
            sourceUrl: emailDisplayUrl,
            dispatchedFilePath: emailDisplayUrl,
            // Parent email file-info can carry markdown preview URLs; for an
            // attachment favorite we must type/load from the attachment itself.
            fileInfo: undefined,
            mediaContext: {
              ...mediaContext,
              dispatchedFilePath: emailDisplayUrl,
              attachmentUrl: emailDisplayUrl,
            },
          }
        : {};
      return {
        ...stripped,
        ...attachmentPinnedFields,
        originalUrl: emailDisplayUrl,
        mediaUrl: emailDisplayUrl,
        mediaSrc: emailDisplayUrl,
      } as MediaSource;
    }
    // True mbox with a reliable index: MediaDisplay's /mbox loader can show
    // that message while fetchEmailCitation fills the body + attachments.
    // Without an index, do not pass the mailbox — /mbox would guess message 0.
    if (
      emailFetchArgs?.kind === "mbox" &&
      emailFetchArgs.emailIndex != null &&
      !isFavoritedEmailAttachment
    ) {
      return {
        ...entry,
        originalUrl: emailFetchArgs.containerUrl,
        mediaContext: {
          ...mediaContext,
          email_index: emailFetchArgs.emailIndex,
          emailIndex: emailFetchArgs.emailIndex,
          ...(emailFetchArgs.previewUrl ? { previewUrl: emailFetchArgs.previewUrl } : {}),
        },
      } as MediaSource;
    }
    return {
      ...stripped,
      originalUrl: undefined,
      mediaUrl: stripMailboxUrl(entry.mediaUrl),
      mediaSrc: stripMailboxUrl(entry.mediaSrc),
    } as MediaSource;
  }, [
    entry,
    emailUrl,
    emailFileInfo,
    isEmailSource,
    isFavoritedEmailAttachment,
    emailDisplayUrl,
    emailFetchArgs,
  ]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 pb-1">
      <div className={`flex flex-row gap-1 shrink-0 pb-2 mb-2 ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS}`}>
        <TpTooltip content={t("favorites.downloadSource")} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style="text" color="light" background="inverse" className={isRestrictedFileType(sourceForActions) ? "text-[var(--text-inverse)]/20" : undefined} onClick={handleDownload} disabled={isRestrictedFileType(sourceForActions)} aria-label={t("favorites.downloadSource")}>
            <DownloadSimpleIcon />
          </Button>
        </TpTooltip>
        <TpTooltip content={t("favorites.addToScope")} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style={addedToScopeId === entry.id ? "solid" : "text"} color={addedToScopeId === entry.id ? "accent" : "light"} background="inverse" onClick={handleAddToScope} disabled={!caseId} aria-label={t("favorites.addToScope")}>
            {addedToScopeId === entry.id ? <CheckIcon /> : <FunnelIcon />}
          </Button>
        </TpTooltip>
        <TpTooltip content={copiedPathId === entry.id ? "Copied" : "Copy source file path"} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style={copiedPathId === entry.id ? "solid" : "text"} color={copiedPathId === entry.id ? "accent" : "light"} background="inverse" onClick={handleCopyPath} aria-label={copiedPathId === entry.id ? "Copied" : "Copy source file path"}>
            {copiedPathId === entry.id ? <CheckIcon /> : <FileCloudIcon />}
          </Button>
        </TpTooltip>
        <TpTooltip content={createdEventId === entry.id ? "Event created" : "Create timeline event from source"} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style={createdEventId === entry.id ? "solid" : "text"} color={createdEventId === entry.id ? "accent" : "light"} background="inverse" onClick={handleCreateEvent} disabled={!onAddToTimeline || !caseId} aria-label={createdEventId === entry.id ? "Event created" : "Create timeline event from source"}>
            {createdEventId === entry.id ? <CheckIcon /> : <ListPlusIcon />}
          </Button>
        </TpTooltip>
        <TpTooltip content={eventSharedSourceAddReady ? "Add this source to Selected Sources in the open event editor" : "Open an event for editing to add this source"} placement="bottom" delayDuration={0}>
          <Button type="button" iconOnly size="sm" style={justAddedToEventSources ? "solid" : "text"} color={justAddedToEventSources ? "accent" : "light"} background="inverse" className={!eventSharedSourceAddReady ? "text-[var(--text-inverse)]/20" : undefined} onClick={handleAddToEventSources} disabled={!eventSharedSourceAddReady} aria-label={t("favorites.addToEvent")}>
            {justAddedToEventSources ? <CheckIcon /> : <NewspaperClippingIcon weight="duotone" />}
          </Button>
        </TpTooltip>
        <TpTooltip content={t("favorites.remove")} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style="text" color="danger" background="inverse" onClick={onRemove} aria-label={t("favorites.remove")}>
            <TrashIcon weight="duotone" />
          </Button>
        </TpTooltip>
        {onClose && (
          <>
            <div className="flex-1" />
            <TpTooltip content={t("favorites.close")} placement="bottom" delayDuration={0}>
              <Button type="button" iconOnly size="sm" style="text" color="light" background="inverse" onClick={onClose} aria-label={t("favorites.close")}>
                <XIcon weight="bold" />
              </Button>
            </TpTooltip>
          </>
        )}
      </div>

      {(hasPageLabel || hasCitationBody) && (
        <div className={`flex flex-row items-top justify-start gap-1.5 w-full pb-2 mb-2 ${splitWithMedia ? "min-h-0 overflow-hidden" : "shrink-0"} ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS}`}>
          {hasCitationBody && (
            <div className="cursor-pointer w-[20px] h-[20px] pl-1 pr-2" onClick={() => setIsCitationExpanded((p) => !p)}>
              {isCitationExpanded ? <CaretDoubleUpIcon size={20} /> : <CaretDoubleDownIcon size={20} />}
            </div>
          )}
          <div className={`box-border flex flex-col gap-0 flex-1 min-w-0 ${
            hasCitationBody
              ? isCitationExpanded
                ? "min-h-0 overflow-y-auto custom-scrollbar2"
                : "h-[21px] max-h-[21px] overflow-hidden opacity-0 transition-opacity duration-300 ease-in-out"
              : ""
          }`}>
            {hasPageLabel && (
              <div className="text-[var(--text-inverse)] font-normal text-base mb-2">Page {entry.page}</div>
            )}
            {hasCitationBody && (
              <div className="flex flex-col gap-2">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={markdownRehypeSanitizePlugins}
                  components={{
                    p: ({ children }) => <span className="mb-2 leading-normal">{children}</span>,
                    h2: ({ children }) => <span className="mb-2 leading-normal">{children}</span>,
                    li: ({ children }) => <li className="mb-2 leading-normal">{children}</li>,
                    a: ({ children }) => <a className="text-brand-blue-light italic">{children}</a>,
                  }}
                >
                  {entry.content?.replace("```json ", "")}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}

      {/*
        Sizing lives on a wrapper, not on the slot: the slot hardcodes `flex-1`
        and `important: true` in the Tailwind config makes that beat both inline
        styles and a competing shorthand. Longhand utilities here avoid the fight.
        Basis 50% with no shrink is a floor, not a cap — a long citation shrinks
        away above it, a short one lets the preview grow past half.
      */}
      <div
        ref={previewHostRef}
        className={`flex min-h-0 flex-col ${
          isEmailSource
            ? "grow shrink basis-0 overflow-y-auto custom-scrollbar2"
            : splitWithMedia
              ? "grow shrink-0 basis-1/2 overflow-hidden"
              : "grow shrink basis-0 overflow-hidden"
        }`}
      >
        <MediaPreviewSlot fillContainer={!isEmailSource} mediaItem={emailMediaItem}>
          <MediaDisplay
            mediaItem={emailMediaItem}
            sourcesExpanded={false}
            fillContainer={!isEmailSource}
            isSourceCard={isEmailSource || sizedPreview}
            containerWidth={sizedPreview ? previewBox?.width : undefined}
            containerHeight={sizedPreview ? previewBox?.height : undefined}
            caseId={caseId}
          />
        </MediaPreviewSlot>
        {isEmailSource && visibleEmailAttachments.length > 0 && (
          <EmailAttachmentsList
            attachments={visibleEmailAttachments}
            caseId={caseId}
            jsonMediaDisplayEnabled={jsonMediaDisplayEnabled}
            defaultOpenIndex={citedAttachmentOpenIndex}
            citedAnalysisFilePath={citedAttachmentUrl || entry.analysisFilePath}
            citedSourceContext={entry.mediaContext}
            onCopyPath={async (attachment, index) => {
              const path = attachment.attachmentUrl || attachment.fileName || "";
              if (!path) return;
              try {
                await navigator.clipboard.writeText(path);
                setCopiedAttachmentPathIndex(index);
                setTimeout(() => setCopiedAttachmentPathIndex(null), 1500);
              } catch { /* ignore */ }
            }}
            onAddToFavorites={
              mediaShare?.addToMediaShare
                ? (attachment, index) => {
                    mediaShare.addToMediaShare(
                      {
                        sourceFilePath: attachment.attachmentUrl,
                        sourceUrl: attachment.attachmentUrl,
                        originalUrl: entry.originalUrl,
                        analysisFilePath: attachment.attachmentUrl,
                        dispatchedFilePath: attachment.attachmentUrl,
                        sourceContext: {
                          ...entry.mediaContext,
                          attachmentUrl: attachment.attachmentUrl,
                          originalUrl: entry.originalUrl,
                          dispatchedFilePath:
                            emailFetchArgs?.kind === "eml"
                              ? emailFetchArgs.emlUrl
                              : entry.mediaContext?.dispatchedFilePath,
                          email_index:
                            entry.mediaContext?.email_index ??
                            entry.mediaContext?.emailIndex,
                        },
                      },
                      () => {
                        setAddedAttachmentFavoriteIndex(index);
                        setTimeout(() => setAddedAttachmentFavoriteIndex(null), 1500);
                      },
                    );
                  }
                : undefined
            }
            onCreateEvent={
              onAddToTimeline && caseId
                ? (attachment, index) => {
                    const source = {
                      sourceFilePath: attachment.attachmentUrl,
                      sourceUrl: attachment.attachmentUrl,
                      sourceContext: { attachmentUrl: attachment.attachmentUrl },
                    } as Source;
                    onAddToTimeline({
                      answerText: "FROM_SOURCE",
                      sources: [source],
                      media: [{
                        mediaUrl: attachment.attachmentUrl,
                        mediaSrc: attachment.attachmentUrl,
                        sourceFilePath: attachment.attachmentUrl,
                      } as MediaSource],
                      autogen: false,
                      epochTimeSecs: Math.floor(Date.now() / 1000),
                    });
                    setCreatedAttachmentEventIndex(index);
                    setTimeout(() => setCreatedAttachmentEventIndex(null), 1500);
                  }
                : undefined
            }
            copiedPathIndex={copiedAttachmentPathIndex}
            addedToFavoritesIndex={addedAttachmentFavoriteIndex}
            createdEventIndex={createdAttachmentEventIndex}
          />
        )}
      </div>
    </div>
  );
}

function NoteDetailView({
  note,
  onUpdateNote,
  onDeleteNote,
  onClose,
}: {
  note: ResearchNote;
  onUpdateNote: (id: string, updates: { title?: string; content?: string }) => void;
  onDeleteNote: (id: string) => void;
  onClose?: () => void;
}) {
  const t = useTimePilotT();
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 pb-1">
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <React.Suspense fallback={<div className="text-[var(--text-inverse)]/60 text-sm">{t("researchNotes.loadingEditor")}</div>}>
          <MarkdownEditor
            key={note.id}
            id={`research-note-${note.id}`}
            title={note.title}
            titleAriaLabel={t("researchNotes.noteTitle")}
            titlePlaceholder={t("researchNotes.noteTitle")}
            onTitleChange={(title) => onUpdateNote(note.id, { title })}
            onTitleBlur={(title) => {
              if (!title.trim()) onUpdateNote(note.id, { title: t("researchNotes.untitled") });
            }}
            markdown={note.content}
            onChange={(content) => onUpdateNote(note.id, { content })}
            debounceMs={300}
            onDelete={() => onDeleteNote(note.id)}
            deleteAriaLabel={t("favorites.remove")}
            onClose={onClose}
            closeAriaLabel={t("favorites.close")}
          />
        </React.Suspense>
      </div>
    </div>
  );
}

function CopyDetailView({
  entry,
  copiedEntryId,
  onCopyEntry,
  onRemove,
  onClose,
  applyTextToEventDescription,
  eventDescriptionApplyReady = false,
}: {
  entry: CopyHistoryEntry;
  copiedEntryId: string | null;
  onCopyEntry: (entry: CopyHistoryEntry) => void;
  onRemove: () => void;
  onClose?: () => void;
  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;
}) {
  const t = useTimePilotT();
  const [appliedId, setAppliedId] = useState<string | null>(null);

  const handleApply = useCallback(() => {
    if (!applyTextToEventDescription || !eventDescriptionApplyReady) return;
    const ok = applyTextToEventDescription(entry.text);
    if (!ok) return;
    setAppliedId(entry.id);
    setTimeout(() => setAppliedId(null), 1500);
  }, [entry, applyTextToEventDescription, eventDescriptionApplyReady]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 pb-3">
      <div className={`flex flex-row gap-1 shrink-0 pb-2 mb-2 ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS}`}>
        <TpTooltip content={copiedEntryId === entry.id ? t("recentlyCopied.copied") : t("favorites.copy")} placement="bottom" delayDuration={0}>
          <Button
            iconOnly
            size="sm"
            style={copiedEntryId === entry.id ? "solid" : "text"}
            color={copiedEntryId === entry.id ? "accent" : "light"}
            background="inverse"
            onClick={() => onCopyEntry(entry)}
            aria-label={t("favorites.copy")}
          >
            {copiedEntryId === entry.id ? <CheckIcon /> : <CopyIcon weight="duotone" />}
          </Button>
        </TpTooltip>
        <TpTooltip content={eventDescriptionApplyReady ? t("recentlyCopied.applyReady") : t("recentlyCopied.applyNeedsEvent")} placement="bottom" delayDuration={0}>
          <Button
            type="button"
            iconOnly
            size="sm"
            style={appliedId === entry.id ? "solid" : "text"}
            color={appliedId === entry.id ? "accent" : "light"}
            background="inverse"
            className={!eventDescriptionApplyReady ? "text-[var(--text-inverse)]/20" : undefined}
            onClick={handleApply}
            disabled={!eventDescriptionApplyReady}
            aria-label={t("favorites.applyDescription")}
          >
            {appliedId === entry.id ? <CheckIcon /> : <Article size={20} weight="duotone" />}
          </Button>
        </TpTooltip>
        <TpTooltip content={t("favorites.remove")} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style="text" color="danger" background="inverse" onClick={onRemove} aria-label={t("favorites.remove")}>
            <TrashIcon weight="duotone" />
          </Button>
        </TpTooltip>
        {onClose && (
          <>
            <div className="flex-1" />
            <TpTooltip content={t("favorites.close")} placement="bottom" delayDuration={0}>
              <Button type="button" iconOnly size="sm" style="text" color="light" background="inverse" onClick={onClose} aria-label={t("favorites.close")}>
                <XIcon weight="bold" />
              </Button>
            </TpTooltip>
          </>
        )}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2 text-[var(--text-inverse)]/90 text-sm break-words leading-relaxed">
        <ReactMarkdown rehypePlugins={markdownRehypeRawSanitizePlugins} remarkPlugins={[remarkGfm]}>
          {entry.text}
        </ReactMarkdown>
        <p className="text-[var(--text-inverse)]/40 text-[11px] mt-3">{dayjs(entry.timestamp).fromNow()}</p>
      </div>
    </div>
  );
}

function CaptureDetailView({
  entry,
  onRemoveCapture,
  onClose,
}: {
  entry: ToolsSharedCaptureEntry;
  onRemoveCapture: (id: string) => void;
  onClose?: () => void;
}) {
  const t = useTimePilotT();
  const toolsDrawer = useToolsDrawer();
  const applyCaptureToEventMedia = toolsDrawer?.applyCaptureToEventMedia;
  const eventMediaCaptureApplyReady = toolsDrawer?.eventMediaCaptureApplyReady ?? false;
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [showObfuscatedPreview, setShowObfuscatedPreview] = useState(true);
  const { canSeeCsamTags, canSeeDescriptions, canRevealOriginal } = useCsamAccess();

  // Always default to the obfuscated preview whenever a different capture is opened,
  // so a prior "Original" toggle never carries over to newly viewed CSAM content.
  useEffect(() => { setShowObfuscatedPreview(true); }, [entry.id]);

  const obfuscatedUrl = obfuscatedWebpEntryToDataUrl(entry);
  const isCsam = canSeeCsamTags && !!entry.contentModerationType && !!obfuscatedUrl;
  // Non-reviewers are always pinned to the obfuscated preview.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;
  const displayUrl =
    isCsam && effectiveShowObfuscated ? (obfuscatedUrl as string) : captureEntryDisplayUrl(entry);

  const copyDisplayUrl = useCallback(() => {
    navigator.clipboard.writeText(displayUrl).then(() => {
      setCopiedId(entry.id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => { /* ignore */ });
  }, [entry, displayUrl]);

  const applyToEvent = useCallback(() => {
    if (!applyCaptureToEventMedia || !eventMediaCaptureApplyReady) return;
    const imageRef =
      isCsam && effectiveShowObfuscated ? displayUrl : captureEntryImageRef(entry);
    if (!imageRef) return;
    const ok = applyCaptureToEventMedia(imageRef);
    if (!ok) return;
    setApplyingId(entry.id);
    setTimeout(() => setApplyingId(null), 1000);
  }, [
    entry,
    displayUrl,
    isCsam,
    effectiveShowObfuscated,
    applyCaptureToEventMedia,
    eventMediaCaptureApplyReady,
  ]);

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden px-3 pb-3">
      <div className={`flex flex-row gap-1 shrink-0 pb-2 mb-2 ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS}`}>
        <TpTooltip content={copiedId === entry.id ? "Copied" : "Copy display URL"} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style={copiedId === entry.id ? "solid" : "text"} color={copiedId === entry.id ? "accent" : "light"} background="inverse" onClick={copyDisplayUrl} aria-label={t("favorites.copyDisplayUrl")}>
            {copiedId === entry.id ? <CheckIcon /> : <CopyIcon weight="duotone" />}
          </Button>
        </TpTooltip>
        <TpTooltip content={eventMediaCaptureApplyReady ? "Apply capture to event media" : "Open an event for editing"} placement="bottom" delayDuration={0}>
          <Button
            type="button"
            iconOnly
            size="sm"
            style={applyingId === entry.id ? "solid" : "text"}
            color={applyingId === entry.id ? "accent" : "light"}
            background="inverse"
            className={!eventMediaCaptureApplyReady ? "text-[var(--text-inverse)]/20" : undefined}
            disabled={!eventMediaCaptureApplyReady}
            onClick={applyToEvent}
            aria-label={t("favorites.applyMedia")}
          >
            {applyingId === entry.id ? <CheckIcon /> : <FrameCornersIcon weight="duotone" />}
          </Button>
        </TpTooltip>
        <TpTooltip content={t("favorites.remove")} placement="bottom" delayDuration={0}>
          <Button iconOnly size="sm" style="text" color="danger" background="inverse" onClick={() => onRemoveCapture(entry.id)} aria-label={t("favorites.remove")}>
            <TrashIcon weight="duotone" />
          </Button>
        </TpTooltip>
        {onClose && (
          <>
            <div className="flex-1" />
            {isCsam && canRevealOriginal && (
              <ContentModerationPreviewSwitcher
                showObfuscatedPreview={showObfuscatedPreview}
                onToggle={setShowObfuscatedPreview}
              />
            )}
            <TpTooltip content={t("favorites.close")} placement="bottom" delayDuration={0}>
              <Button type="button" iconOnly size="sm" style="text" color="light" background="inverse" onClick={onClose} aria-label={t("favorites.close")}>
                <XIcon weight="bold" />
              </Button>
            </TpTooltip>
          </>
        )}
      </div>
      {isCsam && canSeeDescriptions && (
        <div
          data-testid="capture-image-description"
          className={`shrink-0 pb-2 mb-1 ${SIDE_PANEL_DIVIDER_BOTTOM_CLASS}`}
        >
          <div className="text-[var(--text-inverse)]/30 text-[10px] font-semibold tracking-widest uppercase mb-1.5">
            {t("eventModal.description")}
          </div>
          <p className="text-[var(--text-inverse)]/80 text-sm leading-relaxed">
            {entry.imageDescription?.trim() ? entry.imageDescription : t("favorites.noDescription")}
          </p>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar2 flex items-start justify-center pt-4">
        <img
          src={displayUrl}
          alt={t("favorites.card.capture")}
          className="max-w-full max-h-full object-contain rounded border border-[var(--border-subtle)]"
        />
      </div>
      <p className="text-[var(--text-inverse)]/40 text-[11px] mt-2 shrink-0">
        {dayjs(entry.createdAt).fromNow()}
        {entry.s3Uri?.trim()
          ? ` · S3 · ${entry.s3Uri.split("/").pop() ?? "image"}`
          : entry.webpBase64
            ? ` · ${(entry.webpBase64.length / 1024).toFixed(0)} KB`
            : ""}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main FavoritesList Component
// ---------------------------------------------------------------------------

export interface FavoritesListProps {
  entries: MediaShareEntry[];
  setEntries: React.Dispatch<React.SetStateAction<MediaShareEntry[]>>;
  persistEntries: (next: MediaShareEntry[]) => void;
  caseId?: string;
  onAddToTimeline?: (event: EventFromChat) => void;
  addSharedSourceToEvent?: (source: MediaSource) => boolean;
  eventSharedSourceAddReady?: boolean;

  notes: ResearchNote[];
  onAddNote: () => string;
  onUpdateNote: (id: string, updates: { title?: string; content?: string }) => void;
  onDeleteNote: (id: string) => void;

  copyHistory: CopyHistoryEntry[];
  copiedEntryId: string | null;
  onCopyEntry: (entry: CopyHistoryEntry) => void;
  onRemoveCopyEntry: (entryId: string) => void;

  captures: ToolsSharedCaptureEntry[];
  onRemoveCapture: (id: string) => void;
  captureEnabled?: boolean;

  applyTextToEventDescription?: (text: string) => boolean;
  eventDescriptionApplyReady?: boolean;

  showFilters: boolean;
  onSelectionChange?: (hasSelection: boolean) => void;
  onCountsChange?: (total: number, filtered: number, isFiltered: boolean) => void;
}

export default function FavoritesList({
  entries,
  setEntries,
  persistEntries,
  caseId,
  onAddToTimeline,
  addSharedSourceToEvent,
  eventSharedSourceAddReady,
  notes,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  copyHistory,
  copiedEntryId,
  onCopyEntry,
  onRemoveCopyEntry,
  captures,
  onRemoveCapture,
  captureEnabled = true,
  applyTextToEventDescription,
  eventDescriptionApplyReady,
  showFilters,
  onSelectionChange,
  onCountsChange,
}: FavoritesListProps) {
  const t = useTimePilotT();
  /** Show persisted captures even when LD `capture` is off (e.g. meta-viewer has no FeaturesContext). */
  const showCaptures = captureEnabled || captures.length > 0;

  const availableTypes = useMemo<Set<FavoriteItemType>>(() => {
    const types: FavoriteItemType[] = ["source", "note", "copy"];
    if (showCaptures) types.push("capture");
    return new Set(types);
  }, [showCaptures]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Set<FavoriteItemType>>(new Set(availableTypes));
  const [dateRange, setDateRange] = useState<DateRange>("all");
  const [mobileDetail, setMobileDetail] = useState(false);
  const [pendingCsamId, setPendingCsamId] = useState<string | null>(null);

  const { canSeeCsamTags } = useCsamAccess();

  const isCsamCaptureItem = useCallback(
    (item: FavoriteItem | null | undefined): boolean => {
      if (!canSeeCsamTags || !item || item.type !== "capture") return false;
      const entry = item.data as ToolsSharedCaptureEntry;
      return !!entry.contentModerationType && !!obfuscatedWebpEntryToDataUrl(entry);
    },
    [canSeeCsamTags],
  );

  useEffect(() => {
    setSelectedTypes((prev) => {
      if (showCaptures && !prev.has("capture")) {
        return new Set([...prev, "capture"]);
      }
      if (!showCaptures && prev.has("capture")) {
        const next = new Set(prev);
        next.delete("capture");
        return next;
      }
      return prev;
    });
  }, [showCaptures]);

  const allItems = useMemo(
    () => mergeAllFavorites(entries, notes, copyHistory, showCaptures ? captures : []),
    [entries, notes, copyHistory, captures, showCaptures],
  );

  const filteredItems = useMemo(
    () => filterFavorites(allItems, selectedTypes, dateRange),
    [allItems, selectedTypes, dateRange],
  );

  const selectedItem = useMemo(
    () => filteredItems.find((i) => i.id === selectedId) ?? null,
    [filteredItems, selectedId],
  );

  useEffect(() => {
    if (selectedId && !filteredItems.find((i) => i.id === selectedId)) {
      setSelectedId(null);
      setMobileDetail(false);
    }
  }, [filteredItems, selectedId]);

  useEffect(() => {
    onSelectionChange?.(selectedId != null);
  }, [selectedId, onSelectionChange]);

  const isFiltered = selectedTypes.size < availableTypes.size || dateRange !== "all";

  useEffect(() => {
    onCountsChange?.(allItems.length, filteredItems.length, isFiltered);
  }, [allItems.length, filteredItems.length, isFiltered, onCountsChange]);

  const handleRemoveItem = useCallback((item: FavoriteItem) => {
    switch (item.type) {
      case "source":
        setEntries((prev) => {
          const next = prev.filter((e) => e.id !== item.id);
          persistEntries(next);
          return next;
        });
        break;
      case "note":
        onDeleteNote(item.id);
        break;
      case "copy":
        onRemoveCopyEntry(item.id);
        break;
      case "capture":
        onRemoveCapture(item.id);
        break;
    }
    if (selectedId === item.id) {
      setSelectedId(null);
      setMobileDetail(false);
    }
  }, [selectedId, setEntries, persistEntries, onDeleteNote, onRemoveCopyEntry, onRemoveCapture]);

  const openItem = useCallback((id: string) => {
    setSelectedId(id);
    setMobileDetail(true);
  }, []);

  const handleSelect = useCallback((id: string) => {
    if (id !== selectedId) {
      const item = filteredItems.find((i) => i.id === id);
      if (isCsamCaptureItem(item)) {
        setPendingCsamId(id);
        return;
      }
    }
    openItem(id);
  }, [filteredItems, isCsamCaptureItem, openItem, selectedId]);

  const renderDetail = (onClose?: () => void) => {
    if (!selectedItem) {
      return (
        <div className="flex flex-1 items-center justify-center text-[var(--text-inverse)]/50 text-sm">
          {t("favorites.selectItem")}
        </div>
      );
    }
    switch (selectedItem.type) {
      case "source":
        return (
          <SourceDetailView
            entry={selectedItem.data as MediaShareEntry}
            caseId={caseId}
            onRemove={() => handleRemoveItem(selectedItem)}
            onClose={onClose}
            onAddToTimeline={onAddToTimeline}
            addSharedSourceToEvent={addSharedSourceToEvent}
            eventSharedSourceAddReady={eventSharedSourceAddReady}
          />
        );
      case "note":
        return (
          <NoteDetailView
            note={selectedItem.data as ResearchNote}
            onUpdateNote={onUpdateNote}
            onDeleteNote={onDeleteNote}
            onClose={onClose}
          />
        );
      case "copy":
        return (
          <CopyDetailView
            entry={selectedItem.data as CopyHistoryEntry}
            copiedEntryId={copiedEntryId}
            onCopyEntry={onCopyEntry}
            onRemove={() => handleRemoveItem(selectedItem)}
            onClose={onClose}
            applyTextToEventDescription={applyTextToEventDescription}
            eventDescriptionApplyReady={eventDescriptionApplyReady}
          />
        );
      case "capture":
        return (
          <CaptureDetailView
            entry={selectedItem.data as ToolsSharedCaptureEntry}
            onRemoveCapture={onRemoveCapture}
            onClose={onClose}
          />
        );
    }
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
      {showFilters && (
        <FilterBar
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          dateRange={dateRange}
          setDateRange={setDateRange}
          availableTypes={availableTypes}
          compact={!selectedItem}
        />
      )}

      <div className="flex flex-1 min-h-0 flex-row overflow-hidden">
        {/* Master column */}
        <div className={`flex-col overflow-hidden ${mobileDetail ? "hidden" : "flex"} md:!flex ${selectedItem ? `md:w-[280px] md:shrink-0 md:flex-none md:border-r ${SIDE_PANEL_SPLIT_BORDER_CLASS}` : "w-full md:flex-1"}`}>

          {/* Favorites list column — padding matches Figma Side Panel Application2 (684:2755). */}
        <div
          className={`flex flex-1 min-h-0 flex-col gap-[var(--tp-side-panel-body-py)] overflow-y-auto custom-scrollbar2 ${SIDE_PANEL_LIST_SCROLL_CLASS} ${SIDE_PANEL_FAVORITES_LIST_SCROLL_CLASS}`}
        >
          <Button
            type="button"
            style="solid"
            color="accent"
            background="default"
            size="md"
            leadingIcon={<PlusIcon />}
            onClick={() => {
              const id = onAddNote();
              setSelectedTypes((prev) => {
                if (prev.has("note")) return prev;
                return new Set([...prev, "note"]);
              });
              handleSelect(id);
            }}
            className="w-full shrink-0"
            aria-label={t("favorites.newNote")}
          >
            {t("favorites.newNote")}
          </Button>

          {filteredItems.length === 0 ? (
            <p className="m-0 text-center text-xs text-[var(--text-inverse,#ffffff)]/50">
              {allItems.length === 0
                ? t("favorites.empty")
                : t("favorites.noMatch")}
            </p>
          ) : (
            <>
              <p className="m-0 text-[length:var(--font-size-text-small,14px)] font-medium leading-[var(--line-height-leading-small,20px)] text-[var(--text-placeholder,#9ab0c4)]">
                {isFiltered
                  ? t("favorites.recentCount", { shown: filteredItems.length, total: allItems.length })
                  : t("favorites.recent")}
              </p>
              <ul className={SIDE_PANEL_LIST_CLASS}>
                {filteredItems.map((item) => (
                  <FavoriteCard
                    key={item.id}
                    item={item}
                    isSelected={selectedId === item.id}
                    onSelect={() => handleSelect(item.id)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

        {/* Desktop detail panel */}
        {selectedItem && (
          <div className={`hidden md:flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden ${SIDE_PANEL_DETAIL_INSET_CLASS}`}>
            {renderDetail(() => { setSelectedId(null); setMobileDetail(false); })}
          </div>
        )}
      </div>

      {/* Mobile full-screen detail modal */}
      {mobileDetail && selectedItem && createPortal(
        <div className="md:hidden fixed inset-0 z-[5100] flex flex-col bg-[var(--color-ink-085,#0f1d32)]">
          <div className="flex items-center justify-end px-3 pt-3 shrink-0">
            <Button
              type="button"
              iconOnly
              size="md"
              style="text"
              color="light"
              background="inverse"
              onClick={() => { setMobileDetail(false); setSelectedId(null); }}
              aria-label={t("favorites.closeDetail")}
            >
              <XIcon weight="bold" />
            </Button>
          </div>
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
            {renderDetail()}
          </div>
        </div>,
        document.body,
      )}

      {pendingCsamId && (
        <ContentModerationAckModal
          contentModerationType={
            (allItems.find((i) => i.id === pendingCsamId)?.data as ToolsSharedCaptureEntry | undefined)
              ?.contentModerationType ?? ""
          }
          onCancel={() => setPendingCsamId(null)}
          onAcknowledge={() => {
            const id = pendingCsamId;
            setPendingCsamId(null);
            openItem(id);
          }}
        />
      )}
    </div>
  );
}
